import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';

import { fToNow } from 'src/utils/format-time';
import { getPlatformIconUrl } from 'src/utils/platform-icon';

import { markNotificationAsRead } from 'src/redux/slices/notification';

import Label from 'src/components/label';
import { NotificationActionButton } from 'src/components/notification-action';

// ----------------------------------------------------------------------

export default function NotificationItem ({ notification, onActionClick }) {
  const dispatch = useDispatch();

  const handleMarkAsRead = () => {
    if (notification.readStatus) {
      return;
    }
    dispatch(markNotificationAsRead(notification.id));
  };

  const logo = notification.platform ? getLogo(notification.platform) : null;

  const renderAvatar = (
    <ListItemAvatar>
      {logo ? (
        <Avatar
          src={logo.src}
          sx={{ bgcolor: logo.background }}
          // Logos have different shapes. Shrink each one so it fits whole in the middle of the circle.
          slotProps={{ img: { sx: { width: 28, height: 28, objectFit: 'contain' } } }}
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'background.neutral',
          }}
        >
          <Box
            component="img"
            src={`/assets/icons/notification/${'ic_mail'
              // (notification.type === 'order' && 'ic_order') ||
              //   (notification.type === 'chat' && 'ic_chat') ||
              //   (notification.type === 'mail' && 'ic_mail') ||
              //   (notification.type === 'delivery' && 'ic_delivery')
              }.svg`}
            sx={{ width: 24, height: 24 }}
          />
        </Stack>
      )}
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={notification.message}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
          }
        >
          {fToNow(notification.createdAt)}
          {notification.type}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = !notification.readStatus && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
      }}
    />
  );

  const tagsAction = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
      <Label variant="outlined" color="info">
        {notification.listingTitle}
      </Label>
      <Label variant="outlined" color={notification.type || "info"}>
        {notification.type}
      </Label>
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
      }}
      onClick={handleMarkAsRead}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {notification.listingTitle && tagsAction}

        <NotificationActionButton
          action={notification.action}
          onClick={onActionClick}
          sx={{ mt: 1.5, alignSelf: 'flex-start' }}
        />
      </Stack>
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object,
  onActionClick: PropTypes.func,
};

// ----------------------------------------------------------------------

// The logo in a notification's circle, and the circle's colour. Notifications from Voolist itself
// (merges, bulk jobs) have the platform 'voolist' and show the square "V" logo on a circle of the
// logo's own green, so the square blends into the circle. Every other notification shows its
// marketplace's logo on a neutral circle.
function getLogo (platform) {
  if (platform.toLowerCase() === 'voolist') {
    return { src: '/logo/logo_single.png', background: '#17B978' };
  }
  return { src: getPlatformIconUrl(platform), background: 'background.neutral' };
}
